import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Switch,
    FormControlLabel,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Delete } from '@mui/icons-material';
import { ICategoria } from '../../interfaces/ICategoria';
import { IProduto } from '../../interfaces/IProduto';
import { useProdutos } from '../../hooks/useProdutos';
import { IComplemento } from '../../interfaces/IComplemento';
import { IITemComplemento } from '../../interfaces/IITemComplemento';
import { IMassa } from '../../interfaces/IMassa';
import { IBorda } from '../../interfaces/IBorda';
import { ISabor } from '../../interfaces/ISabor';
import { v4 } from 'uuid';
import { TipoProduto } from '../../enum/TipoProduto';
import { useSnackbar } from '../../../core/contexts/SnackbarContext';
import { CardUploadImagem } from '../../../core/components/CardUploadImagem';
import { MIMETypes } from '../../../core/enum/MIMETypes';

interface CriarItemComplementoDrawerProps {
    handleCancelar: () => void;
    open: boolean;
    categorias: ICategoria[];
    handleRefresh: () => void;
}

type GrupoTipo = 'complemento' | 'massa' | 'borda' | 'sabor';

interface Selecionado {
    categoriaUuid: string;
    produtoUuid: string;
    groupType: GrupoTipo;
    complementoUuid?: string; // apenas quando groupType === 'complemento'
}

interface StagedItem {
    uuid: string;
    descricao: string;
    detalhes?: string;
    imagem?: File;
    usarPrecoPorProduto: boolean;
    preco?: string;
    precoSemDesconto?: string;
    precosPorSelecao?: Record<string, { preco: string; precoSemDesconto: string }>; // keySelecionado -> precos
}

export const CriarItemComplementoDrawer = ({ handleCancelar, open, categorias, handleRefresh }: CriarItemComplementoDrawerProps) => {
    const [filtroDescricao, setFiltroDescricao] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [selecionados, setSelecionados] = useState<Selecionado[]>([]);
    const { salvar, loading } = useProdutos();
    const snackbar = useSnackbar();

    // Modal de criação do item
    const [formOpen, setFormOpen] = useState(false);
    const [descricao, setDescricao] = useState<string>('');
    const [detalhes, setDetalhes] = useState<string>('');
    const [preco, setPreco] = useState<string>('');
    const [precoSemDesconto, setPrecoSemDesconto] = useState<string>('');
    const [imagem, setImagem] = useState<File | undefined>(undefined);
    const [usarPrecoPorProduto, setUsarPrecoPorProduto] = useState<boolean>(false);
    const [precosSelecionados, setPrecosSelecionados] = useState<Record<string, { preco: string; precoSemDesconto: string }>>({});
    const [saving, setSaving] = useState<boolean>(false);
    const [saveProgress, setSaveProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
    const [stagedItems, setStagedItems] = useState<StagedItem[]>([]);

    const mimetypesValidos = [
        MIMETypes["image/jpeg"],
        MIMETypes["image/jpg"],
        MIMETypes["image/png"]
    ] as string[];

    const handleImagemChange = (arquivo: File) => {
        if (!mimetypesValidos.includes(arquivo.type)) {
            return snackbar(`Arquivo inválido, tipos aceitos: (PNG, JPEG)`, { severity: 'error' });
        }
        setImagem(arquivo);
    };

    const handleRemoveImagem = () => setImagem(undefined);

    const clearForm = () => {
        setDescricao('');
        setDetalhes('');
        setPreco('');
        setPrecoSemDesconto('');
        setImagem(undefined);
    }

    const produtos: { categoriaUuid: string; categoriaDescricao: string; produto: IProduto }[] = useMemo(() => {
        if (!open) return [];
        const list: { categoriaUuid: string; categoriaDescricao: string; produto: IProduto }[] = [];
        categorias?.forEach(categoria => {
            if (!categoria.uuid) return; // ignora categorias sem uuid
            categoria?.produtos?.forEach(produto => list.push({ categoriaUuid: categoria.uuid as string, categoriaDescricao: categoria.descricao, produto }));
        });
        return list;
    }, [categorias, open]);

    const produtosFiltrados = useMemo(() => {
        return produtos.filter(({ produto }) => produto.descricao.toLowerCase().includes(filtroDescricao.toLowerCase()));
    }, [produtos, filtroDescricao]);

    const produtosPaginados = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return produtosFiltrados.slice(start, end);
    }, [produtosFiltrados, page, rowsPerPage]);

    const isSelecionado = (sel: Selecionado) => {
        return selecionados.some(s => s.categoriaUuid === sel.categoriaUuid && s.produtoUuid === sel.produtoUuid && s.groupType === sel.groupType && s.complementoUuid === sel.complementoUuid);
    }

    const keySelecionado = (s: Selecionado) => `${s.produtoUuid}:${s.groupType}:${s.complementoUuid ?? ''}`;

    const toggleSelecionado = (sel: Selecionado) => {
        setSelecionados(prev => {
            const exists = prev.find(s => s.categoriaUuid === sel.categoriaUuid && s.produtoUuid === sel.produtoUuid && s.groupType === sel.groupType && s.complementoUuid === sel.complementoUuid);
            if (exists) {
                const updated = prev.filter(s => !(s.categoriaUuid === sel.categoriaUuid && s.produtoUuid === sel.produtoUuid && s.groupType === sel.groupType && s.complementoUuid === sel.complementoUuid));
                if (usarPrecoPorProduto) {
                    const k = keySelecionado(sel);
                    setPrecosSelecionados(state => {
                        const copy = { ...state };
                        delete copy[k];
                        return copy;
                    });
                }
                return updated;
            }
            if (usarPrecoPorProduto) {
                const k = keySelecionado(sel);
                setPrecosSelecionados(state => ({
                    ...state,
                    [k]: {
                        preco: state[k]?.preco ?? (preco || ''),
                        precoSemDesconto: state[k]?.precoSemDesconto ?? (precoSemDesconto || '')
                    }
                }));
            }
            return [...prev, sel];
        });
    }

    const handleCancelarDrawer = () => {
        if (loading) return;
        setFiltroDescricao('');
        setPage(0);
        setRowsPerPage(25);
        setSelecionados([]);
        clearForm();
        setFormOpen(false);
        handleCancelar();
    }

    const handleAbrirFormulario = () => {
        if (!selecionados.length) return snackbar('Nenhum grupo selecionado', { severity: 'error' });
        setFormOpen(true);
    }

    const handleSalvarItem = async () => {
        if (!descricao?.trim()) return snackbar('Descrição inválida', { severity: 'error' });
        if (!usarPrecoPorProduto) {
            const valorGlobal = Number(preco ?? '');
            if (isNaN(valorGlobal)) return snackbar('Preço inválido', { severity: 'error' });
            if (precoSemDesconto?.length && isNaN(Number(precoSemDesconto))) return snackbar('Preço sem desconto inválido', { severity: 'error' });
        } else {
            for (const sel of selecionados) {
                const k = keySelecionado(sel);
                const entry = precosSelecionados[k];
                const valor = Number(entry?.preco ?? '');
                if (isNaN(valor)) return snackbar('Preço inválido para algum produto selecionado', { severity: 'error' });
                if (entry?.precoSemDesconto?.length && isNaN(Number(entry?.precoSemDesconto))) return snackbar('Preço sem desconto inválido para algum produto selecionado', { severity: 'error' });
            }
        }
        // Registrar item em memória e limpar formulário
        const staged: StagedItem = {
            uuid: v4(),
            descricao: descricao.trim(),
            detalhes: detalhes?.trim() || undefined,
            imagem,
            usarPrecoPorProduto,
            preco: usarPrecoPorProduto ? undefined : (preco || ''),
            precoSemDesconto: usarPrecoPorProduto ? undefined : (precoSemDesconto || ''),
            precosPorSelecao: usarPrecoPorProduto ? { ...precosSelecionados } : undefined
        };
        setStagedItems(list => [...list, staged]);
        // prepara próximo cadastro
        clearForm();
        setUsarPrecoPorProduto(false);
        setPrecosSelecionados({});
        setFormOpen(false);
    }

    const handleRemoverItemStaged = (uuid: string) => setStagedItems(list => list.filter(i => i.uuid !== uuid));

    const handleFinalizarCadastro = async () => {
        if (!selecionados.length) return snackbar('Nenhum grupo selecionado', { severity: 'error' });
        if (!stagedItems.length) return snackbar('Nenhum item para cadastrar', { severity: 'error' });

        setSaving(true);

        // Agrupar seleções por produto para minimizar saves
        const porProduto = new Map<string, { categoriaUuid: string; produto: IProduto }>();
        categorias.forEach(cat => {
            if (!cat.uuid) return;
            cat.produtos?.forEach(p => porProduto.set(p.uuid, { categoriaUuid: cat.uuid as string, produto: p }));
        });

        // Copiar produtos para edição isolada
        const produtosParaSalvar = new Map<string, { categoriaUuid: string; produto: IProduto }>();
        selecionados.forEach(sel => {
            const encontrado = porProduto.get(sel.produtoUuid);
            if (encontrado) {
                if (!produtosParaSalvar.has(sel.produtoUuid)) {
                    const clone: IProduto = { ...encontrado.produto };
                    produtosParaSalvar.set(sel.produtoUuid, { categoriaUuid: encontrado.categoriaUuid, produto: clone });
                }
            }
        });

        // Aplicar TODOS os itens staged em cada seleção
        produtosParaSalvar.forEach(({ produto }) => {
            const gruposDesteProduto = selecionados.filter(s => s.produtoUuid === produto.uuid);
            gruposDesteProduto.forEach(s => {
                const k = keySelecionado(s);
                stagedItems.forEach(stg => {
                    const valor = stg.usarPrecoPorProduto
                        ? Number(stg?.precosPorSelecao?.[k]?.preco ?? 0)
                        : Number(stg.preco ?? 0);
                    const valorSemDesc = stg.usarPrecoPorProduto
                        ? (stg?.precosPorSelecao?.[k]?.precoSemDesconto?.length ? Number(stg?.precosPorSelecao?.[k]?.precoSemDesconto) : undefined)
                        : (stg.precoSemDesconto?.length ? Number(stg.precoSemDesconto) : undefined);

                    // criar base de item para este staged
                    const baseImagens = stg.imagem ? [stg.imagem as any] : undefined;

                    if (s.groupType === 'complemento') {
                        const complementos = produto.complementos ?? [];
                        const compIndex = complementos.findIndex(c => c.uuid === s.complementoUuid);
                        if (compIndex !== -1) {
                            // Verificar duplicação
                            const existeItem = complementos[compIndex].itens?.some(i => i.descricao.toLowerCase() === stg.descricao.toLowerCase());
                            if (existeItem) {
                                return; // pula este item, não duplica
                            }
                            const comp: IComplemento = { ...complementos[compIndex] };
                            const novoItem: IITemComplemento = {
                                uuid: v4(),
                                descricao: stg.descricao,
                                detalhes: stg.detalhes,
                                preco: valor,
                                precoSemDesconto: valorSemDesc,
                                imagens: baseImagens
                            } as unknown as IITemComplemento;
                            comp.itens = [...(comp.itens ?? []), novoItem];
                            complementos[compIndex] = comp;
                            produto.complementos = [...complementos];
                        }
                    }
                    if (s.groupType === 'massa') {
                        const massas = produto.massas ?? [];
                        // Verificar duplicação
                        const existeMassa = massas.some(m => m.descricao.toLowerCase() === stg.descricao.toLowerCase());
                        if (existeMassa) {
                            return; // pula, não duplica
                        }
                        const novo: IMassa = {
                            uuid: v4(),
                            descricao: stg.descricao,
                            preco: valor,
                            precoSemDesconto: valorSemDesc,
                            imagens: baseImagens as any,
                            fichaTecnica: undefined,
                        };
                        produto.massas = [...massas, novo];
                    }
                    if (s.groupType === 'borda') {
                        const bordas = produto.bordas ?? [];
                        // Verificar duplicação
                        const existeBorda = bordas.some(b => b.descricao.toLowerCase() === stg.descricao.toLowerCase());
                        if (existeBorda) {
                            return; // pula, não duplica
                        }
                        const novo: IBorda = {
                            uuid: v4(),
                            descricao: stg.descricao,
                            preco: valor,
                            precoSemDesconto: valorSemDesc,
                            imagens: baseImagens as any,
                            fichaTecnica: undefined,
                        };
                        produto.bordas = [...bordas, novo];
                    }
                    if (s.groupType === 'sabor') {
                        const sabores = produto.sabores ?? [];
                        // Verificar duplicação
                        const existeSabor = sabores.some(sb => sb.descricao.toLowerCase() === stg.descricao.toLowerCase());
                        if (existeSabor) {
                            return; // pula, não duplica
                        }
                        const novo: ISabor = {
                            uuid: v4(),
                            descricao: stg.descricao,
                            detalhes: stg.detalhes,
                            preco: valor,
                            precoSemDesconto: valorSemDesc,
                            imagens: baseImagens as any,
                            fichaTecnica: undefined,
                        };
                        produto.sabores = [...sabores, novo];
                    }
                });
            });
        });

        try {
            const toPersist = Array.from(produtosParaSalvar.values());
            setSaveProgress({ current: 0, total: toPersist.length });
            let idx = 0;
            for (const { categoriaUuid, produto } of toPersist) {
                const ok = await salvar(categoriaUuid, produto);
                idx++;
                setSaveProgress({ current: idx, total: toPersist.length });
                if (!ok) {
                    snackbar(`Falha ao salvar produto ${produto.descricao}`, { severity: 'error' });
                }
            }

            snackbar('Itens adicionados com sucesso', { severity: 'success' });
            handleRefresh();
            // limpar estado e fechar
            setStagedItems([]);
            setSelecionados([]);
            clearForm();
            setUsarPrecoPorProduto(false);
            setPrecosSelecionados({});
            setFormOpen(false);
            handleCancelarDrawer();
        } finally {
            setSaving(false);
        }
    }

    return (
        <Drawer anchor="right" open={open} onClose={handleCancelarDrawer} sx={{ '& .MuiDrawer-paper': { width: '900px', padding: '1rem' } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Criar item de complemento</Typography>
                <IconButton onClick={handleCancelarDrawer}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ paddingY: '1rem' }}>
                <TextField
                    label="Filtrar por produto"
                    fullWidth
                    value={filtroDescricao}
                    onChange={e => setFiltroDescricao(e.target.value)}
                />
            </Box>

            <TableContainer component={Box}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell>Produto</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Grupos (selecione os complementos)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {produtosPaginados.map(({ categoriaUuid, categoriaDescricao, produto }) => (
                            <TableRow key={produto.uuid}>
                                <TableCell>
                                    <Typography fontWeight={600}>{produto.descricao}</Typography>
                                    <Typography variant="body2" color="text.secondary">Categoria: {categoriaDescricao}</Typography>
                                </TableCell>
                                <TableCell>{produto.tipo}</TableCell>
                                <TableCell>
                                    <Box display="flex" gap={3} flexWrap="wrap">
                                        {produto.tipo === TipoProduto.pizza && (
                                            <>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Checkbox
                                                        checked={isSelecionado({ categoriaUuid, produtoUuid: produto.uuid, groupType: 'massa' })}
                                                        onChange={() => toggleSelecionado({ categoriaUuid, produtoUuid: produto.uuid, groupType: 'massa' })}
                                                    />
                                                    <Typography>Massa</Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Checkbox
                                                        checked={isSelecionado({ categoriaUuid, produtoUuid: produto.uuid, groupType: 'borda' })}
                                                        onChange={() => toggleSelecionado({ categoriaUuid, produtoUuid: produto.uuid, groupType: 'borda' })}
                                                    />
                                                    <Typography>Borda</Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Checkbox
                                                        checked={isSelecionado({ categoriaUuid, produtoUuid: produto.uuid, groupType: 'sabor' })}
                                                        onChange={() => toggleSelecionado({ categoriaUuid, produtoUuid: produto.uuid, groupType: 'sabor' })}
                                                    />
                                                    <Typography>Sabores</Typography>
                                                </Box>
                                            </>
                                        )}
                                        {produto.tipo === TipoProduto.preparado && (
                                            <>
                                                {(produto.complementos ?? []).map((comp: IComplemento) => (
                                                    <Box key={comp.uuid} display="flex" alignItems="center" gap={1}>
                                                        <Checkbox
                                                            checked={isSelecionado({ categoriaUuid, produtoUuid: produto.uuid, groupType: 'complemento', complementoUuid: comp.uuid })}
                                                            onChange={() => toggleSelecionado({ categoriaUuid, produtoUuid: produto.uuid, groupType: 'complemento', complementoUuid: comp.uuid })}
                                                        />
                                                        <Typography>{comp.descricao}</Typography>
                                                    </Box>
                                                ))}
                                                {!(produto.complementos ?? []).length && (
                                                    <Typography variant="body2" color="text.secondary">Sem complementos configurados</Typography>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={produtosFiltrados.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Linhas por página"
                />
            </TableContainer>

            {/* Lista de itens pendentes */}
            <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1">Itens para cadastrar ({stagedItems.length})</Typography>
                {stagedItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nenhum item adicionado ainda</Typography>
                ) : (
                    <Table size="small" sx={{ mt: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Precificação</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stagedItems.map(item => (
                                <TableRow key={item.uuid}>
                                    <TableCell>{item.descricao}</TableCell>
                                    <TableCell>
                                        {item.usarPrecoPorProduto ? 'Por produto' : `Global: ${item.preco ?? ''}${item.precoSemDesconto ? ` / s/ desc: ${item.precoSemDesconto}` : ''}`}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleRemoverItemStaged(item.uuid)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            <Box display="flex" justifyContent="flex-end" alignItems={'center'} gap={2} mt={2}>
                <Button variant='outlined' disabled={loading || saving} onClick={handleCancelarDrawer}>Cancelar</Button>
                <Button variant='outlined' disabled={selecionados.length === 0 || loading || saving} onClick={handleAbrirFormulario}>
                    Adicionar item
                </Button>
                <Button variant='contained' color='primary' disabled={stagedItems.length === 0 || selecionados.length === 0 || loading || saving} onClick={handleFinalizarCadastro}>
                    {saving ? (
                        <>
                            <CircularProgress size={18} sx={{ mr: 1 }} />
                            Finalizando {saveProgress.current}/{saveProgress.total}
                        </>
                    ) : (
                        'Finalizar'
                    )}
                </Button>
            </Box>

            <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth PaperProps={{ sx: { width: 920, maxWidth: '920px', height: '80vh' } }}>
                <DialogTitle>Cadastrar item</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflowY: 'auto' }}>
                    <TextField
                        sx={{ mt: 2 }}
                        label="Descrição"
                        placeholder="Ex: Bacon, Carne extra, etc..."
                        value={descricao}
                        onChange={(e) => setDescricao(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        sx={{ mt: 2 }}
                        label="Detalhes"
                        placeholder="Detalhes"
                        value={detalhes}
                        onChange={(e) => setDetalhes(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={12}
                    />
                    <FormControlLabel
                        sx={{ mt: 2 }}
                        control={<Switch checked={usarPrecoPorProduto} onChange={() => setUsarPrecoPorProduto(!usarPrecoPorProduto)} />}
                        label="Definir preços por produto"
                    />
                    {!usarPrecoPorProduto && (
                        <Box display={'flex'} gap={2} sx={{ mt: 2 }}>
                            <TextField
                                label="Preço"
                                placeholder="0,00"
                                value={preco}
                                onChange={(e) => setPreco(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Preço sem desconto"
                                placeholder="0,00"
                                value={precoSemDesconto}
                                onChange={(e) => setPrecoSemDesconto(e.target.value)}
                                fullWidth
                            />
                        </Box>
                    )}
                    {usarPrecoPorProduto && (
                        <Box sx={{ mt: 2 }}>
                            {selecionados.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Nenhum grupo selecionado</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Produto</TableCell>
                                            <TableCell>Grupo</TableCell>
                                            <TableCell>Preço</TableCell>
                                            <TableCell>Preço sem desconto</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selecionados.map(s => {
                                            const k = keySelecionado(s);
                                            const prod = produtos.find(p => p.produto.uuid === s.produtoUuid);
                                            return (
                                                <TableRow key={k}>
                                                    <TableCell>{prod?.produto.descricao ?? s.produtoUuid}</TableCell>
                                                    <TableCell>{s.groupType === 'complemento' ? 'Complemento' : s.groupType ? s.groupType.charAt(0).toUpperCase() + s.groupType.slice(1) : ''}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            placeholder="0,00"
                                                            value={precosSelecionados[k]?.preco ?? ''}
                                                            onChange={(e) => setPrecosSelecionados(state => ({
                                                                ...state,
                                                                [k]: {
                                                                    preco: e.target.value,
                                                                    precoSemDesconto: state[k]?.precoSemDesconto ?? ''
                                                                }
                                                            }))}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            placeholder="0,00"
                                                            value={precosSelecionados[k]?.precoSemDesconto ?? ''}
                                                            onChange={(e) => setPrecosSelecionados(state => ({
                                                                ...state,
                                                                [k]: {
                                                                    preco: state[k]?.preco ?? '',
                                                                    precoSemDesconto: e.target.value
                                                                }
                                                            }))}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    )}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <CardUploadImagem
                            titulo="Imagem"
                            descricao={imagem ? imagem.name : 'Faça o upload'}
                            url={imagem ? URL.createObjectURL(imagem) : undefined}
                            excluir={handleRemoveImagem}
                            upload={handleImagemChange}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant='outlined' onClick={() => setFormOpen(false)} disabled={loading || saving}>Cancelar</Button>
                    <Button variant='contained' onClick={handleSalvarItem} disabled={loading || saving}>
                        {saving ? (
                            <>
                                <CircularProgress size={18} sx={{ mr: 1 }} />
                                Salvando {saveProgress.current}/{saveProgress.total}
                            </>
                        ) : (
                            'Salvar'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
}


